import React, {useEffect, useState} from 'react';
import {loginResponseSelector} from '../../../redux/selectors/login';
import {useSelector} from 'react-redux';
import {useAppDispatch, useAppSelector} from '../../../redux';
import {reduxForm, getFormValues, SubmissionError, SubmitHandler, change} from 'redux-form';

import {Forms} from '../../../shared/models/forms';
import {PhoneNumberCheckForm} from '../../../shared/models/login';
import {validationPipe, ValidationItem, onlyDigitsPipe} from '../../../shared/pipes';
import {LoginType, RequestedOperationType} from '../../../shared/models/api';
import {RegExps} from '../../../shared/constants/regexps';
import {ServerValidationError} from '../../../shared/models/serverValidationError';
import {startDigits} from '../../../shared/constants';
import {checkCode, createNewPassword, sendCodeWithCheckPhone, resetLoginState} from '../../../redux/actions/login';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import View from './view';
import {defaults} from '../../../shared/constants/defaults';
import {passwordValidator, passwordValidatorMessage} from '../../../shared/utils/validators';
import {setRuntimeLoginStepState} from '../../../redux/actions/runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {profileSlice} from '../../../redux/reducers/profile';

const getFormErrors = (
  formData: PhoneNumberCheckForm,
  confirmByCard: boolean,
  confirmBySnils: boolean,
  needPassword: boolean,
  needPhone: boolean,
) => {
  if (needPassword) {
    return validationPipe(formData, [
      {
        names: 'password',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'password',
        error: passwordValidatorMessage,
        type: 'custom',
        validator: passwordValidator,
      },
      {
        names: 'confirm',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'confirm,password',
        error: 'Поле не совпадает с паролем',
        type: 'equals',
      },
    ]);
  }
  if (needPhone) {
    return validationPipe(formData, [
      {
        names: 'phone',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'phone',
        error: 'Номер телефона должен состоять из 10 цифр',
        type: 'custom',
        validator: (value) => {
          return `${value}`.replace(/\D/g, '').length === 11;
        },
      },
    ]);
  }
  if (confirmByCard) {
    return validationPipe(formData, [
      {
        names: 'phone',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'phone',
        error: 'Номер телефона должен состоять из 10 цифр',
        type: 'custom',
        validator: (value) => {
          const valueLength = `${value}`.replace(/\D/g, '').length;
          return valueLength === 11;
        },
      },
      {
        names: 'snils',
        error: 'Обязательное поле',
        type: 'required',
      },
      {
        names: 'snils',
        error: 'Длина СНИЛС должна равняться 11 цифрам',
        type: 'custom',
        validator: (value) => {
          const valueLength = `${value}`.replace(/\D/g, '').length;
          return valueLength === 11;
        },
      },
    ]);
  } else if (confirmBySnils) {
    const baseValidationArray: ValidationItem[] = [];
    const inputCardValue = formData?.cardNumber ?? '';

    const startCardDigits = inputCardValue.startsWith(startDigits);
    const isCardDigitsLength = inputCardValue.length < 16;
    const isMaxCardDigitsLength = inputCardValue.length === 16;

    if (!inputCardValue.length) {
      baseValidationArray.push({
        names: 'cardNumber',
        error: 'Обязательное поле',
        type: 'required',
      });
    }
    if (isCardDigitsLength && startCardDigits) {
      baseValidationArray.push({
        names: 'cardNumber',
        error: 'Длина номера должна равняться 16 символам',
        type: 'pattern',
        pattern: RegExps.checkEsksoCardNumber,
      });
    }
    if ((isCardDigitsLength || isMaxCardDigitsLength) && !startCardDigits) {
      baseValidationArray.push({
        names: 'cardNumber',
        error: `Номер карты должен начинаться с ${startDigits}`,
        type: 'pattern',
        pattern: RegExps.checkEsksoCardNumber,
      });
    }
    return validationPipe(formData, baseValidationArray);
  }
};

const isSmsCodeFilled: (smsCode: string) => boolean = (smsCode) => smsCode?.length === defaults.smsCodeLength;

export interface Props {
  handleSubmit: SubmitHandler<PhoneNumberCheckForm, PhoneNumberCheckForm, string>;
}

export const PhoneNumberCheck_ = (props: Props) => {
  const {handleSubmit} = props;

  const dispatch = useAppDispatch();
  const loginResponse = useSelector(loginResponseSelector());
  const [submitting, setSubmitting] = useState<boolean>(false);

  const confirmByCard = !!(loginResponse && loginResponse.loginType === LoginType.EskNumber);
  const confirmBySnils = !!(loginResponse && loginResponse.loginType === LoginType.Snils);

  const [needPhone, setPhoneNumber] = useState<boolean>(false);
  const [needSmsCode, setSmsCode] = useState<boolean>(false);
  const [needPassword, setPassword] = useState<boolean>(false);
  const [needAgreement, setAgreement] = useState<boolean>(false);

  const formValues: PhoneNumberCheckForm = useAppSelector((state) =>
    getFormValues(Forms.phoneNumberCheckForm)(state),
  ) as PhoneNumberCheckForm;

  const [isConfirmButtonDisabled, setConfirmButtonDisabled] = useState(!isSmsCodeFilled(formValues?.smsCode ?? ''));
  useEffect(() => setConfirmButtonDisabled(!isSmsCodeFilled(formValues?.smsCode ?? '')), [formValues]);

  const [disabled, setDisabled] = useState<boolean>(false);
  useEffect(() => {
    setDisabled((needAgreement && !formValues.agreement) || false);
  }, [needAgreement, needSmsCode, formValues]);

  const onMainClick = async (formData: PhoneNumberCheckForm) => {
    const submissionError = getFormErrors(formData, confirmByCard, confirmBySnils, needPassword, needPhone);
    if (submissionError) {
      throw new SubmissionError(submissionError);
    }

    if (formData.password) {
      setSubmitting(true);
      try {
        await dispatch(createNewPassword(formData.smsCode || '', formData.password || '', true));
        setPassword(false);
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0].message || error.message || 'Ошибка. Повторите еще раз';
        dispatch(
          addNotification({
            message: errorText,
            type: SnackbarNotificationType.Error,
          }),
        );
      } finally {
        setSubmitting(false);
      }
    } else if (formData.agreement) {
      setAgreement(false);
      if (!loginResponse?.isPasswordSet) {
        setPassword(true);
      } else {
        dispatch(setRuntimeLoginStepState(LoginState.enter));
        dispatch(resetLoginState());
        dispatch(profileSlice.actions.setSessionState(false));
      }
    } else if (formData.smsCode) {
      setSubmitting(true);
      setPhoneNumber(false);
      try {
        await dispatch(
          checkCode(
            formData.smsCode || '',
            onlyDigitsPipe(formData.phone || ''),
            RequestedOperationType.SetPhoneNumberConfirmation,
          ),
        );
        setSmsCode(false);
        setAgreement(true);
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0].message || error.message || 'Ошибка. Повторите еще раз';
        dispatch(
          addNotification({
            message: errorText,
            type: SnackbarNotificationType.Error,
          }),
        );
      } finally {
        setSubmitting(false);
      }
    } else {
      if (confirmBySnils) {
        try {
          setSubmitting(true);
          setPhoneNumber(true);
          if (needPhone) {
            await dispatch(
              sendCodeWithCheckPhone(
                onlyDigitsPipe(formData.cardNumber || ''),
                onlyDigitsPipe(formData.identificator || ''),
                onlyDigitsPipe(formData.phone || ''),
                true,
              ),
            );

            setPhoneNumber(false);
            setSmsCode(true);
          }
        } catch (e) {
          const error = e as ServerValidationError;
          let errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
          dispatch(
            addNotification({
              message: errorText,
              type: SnackbarNotificationType.Error,
            }),
          );
        } finally {
          setSubmitting(false);
        }
      } else if (confirmByCard) {
        try {
          setSubmitting(true);
          await dispatch(
            sendCodeWithCheckPhone(
              formData.identificator || '',
              onlyDigitsPipe(formData.snils || ''),
              onlyDigitsPipe(formData.phone || ''),
              true,
            ),
          );
          setPhoneNumber(false);
          setSmsCode(true);
        } catch (e) {
          const error = e as ServerValidationError;
          let errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
          dispatch(
            addNotification({
              message: errorText,
              type: SnackbarNotificationType.Error,
            }),
          );
        } finally {
          setSubmitting(false);
        }
      }
    }
  };

  const handleAutoFill = (fieldName: string) => (data: any) => {
    dispatch(change(Forms.phoneNumberCheckForm, fieldName, data));
    setTimeout(handleSubmit(onMainClick), 0);
  };
  return (
    <View
      disabled={disabled}
      onAutoFill={handleAutoFill}
      onSubmit={(e) => {
        e?.preventDefault();
        handleSubmit(onMainClick)();
      }}
      onRefresh={async () => {
        try {
          await dispatch(
            sendCodeWithCheckPhone(
              onlyDigitsPipe((formValues.cardNumber ? formValues.cardNumber : formValues.identificator) || ''),
              onlyDigitsPipe((formValues.snils ? formValues.snils : formValues.identificator) || ''),
              onlyDigitsPipe(formValues.phone ?? ''),
              true,
            ),
          );
          return true;
        } catch {
          return false;
        }
      }}
      confirmByCard={confirmByCard}
      confirmBySnils={confirmBySnils}
      needPhone={needPhone}
      needSmsCode={needSmsCode}
      needPassword={needPassword}
      needAgreement={needAgreement}
      loginType={loginResponse?.loginType}
      submitting={submitting}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
    />
  );
};

export const PhoneCheck = reduxForm<PhoneNumberCheckForm, {}>({
  form: Forms.phoneNumberCheckForm,
})(PhoneNumberCheck_);
